import { MessageSquare, Plus, Settings } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../../tailwind/badge";
import { Button } from "../../tailwind/button";
import { Input } from "../../tailwind/input";
import { Label } from "../../tailwind/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../tailwind/select";
import {
  Sheet,
  SheetContent,
} from "../../tailwind/sheet";
import { getCampaignNameById } from "../../../pages/whatsapp/utils/contactsUtils";
import {
  hasContactValidationErrors,
  validateUpdateContactForm,
} from "../../../pages/whatsapp/utils/contactsValidation";
import ContactActivityLog from "./ContactActivityLog";

function FieldError({ message }) {
  if (!message) return null;
  return <p className="text-xs text-red-600 dark:text-red-400 mt-1">{message}</p>;
}

function getInitials(name) {
  return name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export default function ContactProfileSheet({
  open,
  onOpenChange,
  contact,
  leadStatusOptions = [],
  onOpenAttributeLibrary,
  onSave,
}) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [leadStatus, setLeadStatus] = useState("");
  const [tagsText, setTagsText] = useState("");
  const [profileErrors, setProfileErrors] = useState({});

  useEffect(() => {
    if (!contact) return;
    setEmail(contact.email || "");
    setLeadStatus(contact.leadStatus || "");
    setTagsText((contact.tags || []).join(", "));
    setProfileErrors({});
  }, [contact]);

  const leadStatusOptionsList = useMemo(() => {
    const options = [...leadStatusOptions];
    if (
      contact?.leadStatus &&
      !options.some((option) => option.value === contact.leadStatus)
    ) {
      options.unshift({ value: contact.leadStatus, label: contact.leadStatus });
    }
    return options;
  }, [contact, leadStatusOptions]);

  if (!contact) return null;

  const handleSave = () => {
    const errors = validateUpdateContactForm({ email });
    if (hasContactValidationErrors(errors)) {
      setProfileErrors(errors);
      return;
    }

    setProfileErrors({});
    onSave?.({
      email,
      leadStatus,
      tags: tagsText,
    });
  };

  const selectedLeadStatusValue =
    leadStatusOptionsList.find(
      (option) =>
        option.label.toLowerCase() === String(leadStatus || "").toLowerCase() ||
        option.value === leadStatus
    )?.value || leadStatus || leadStatusOptionsList[0]?.value || "";

  const handleLiveChat = () => {
    onOpenChange(false);
    navigate("/whatsapp/chat", { state: { phone: contact.phone } });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md w-full overflow-y-auto bg-slate-50 dark:bg-slate-950 p-0 border-gray-200 dark:border-slate-800">
        <div className="h-full flex flex-col">
          <div className="bg-white dark:bg-slate-900 p-6 border-b border-gray-200 dark:border-slate-800">
            <div className="flex items-center gap-4 mb-4 pr-8">
              <div className="h-16 w-16 rounded-full bg-emerald-100 dark:bg-emerald-950/50 flex items-center justify-center text-emerald-600 dark:text-emerald-400 text-xl font-bold">
                {getInitials(contact.name)}
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {contact.name}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">{contact.phone}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                className="w-full text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
                onClick={handleLiveChat}
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Live Chat
              </Button>
              <Button variant="outline" className="w-full border-gray-200 dark:border-slate-700" onClick={handleSave}>
                Save Changes
              </Button>
            </div>
          </div>

          <div className="p-6 space-y-6 flex-1">
            <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm space-y-4">
              <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                Contact Details
              </h3>

              <div>
                <Label className="text-xs text-gray-500 dark:text-gray-400">Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    if (profileErrors.email) {
                      setProfileErrors((current) => ({ ...current, email: '' }));
                    }
                  }}
                  className={`h-8 text-sm mt-1 bg-white dark:bg-slate-950 border-gray-200 dark:border-slate-800 ${
                    profileErrors.email ? "border-red-500 dark:border-red-500" : ""
                  }`}
                  aria-invalid={Boolean(profileErrors.email)}
                />
                <FieldError message={profileErrors.email} />
              </div>

              <div>
                <Label className="text-xs text-gray-500 dark:text-gray-400">Lead Status</Label>
                {leadStatusOptionsList.length > 0 ? (
                  <Select
                    value={selectedLeadStatusValue}
                    onValueChange={(value) => {
                      const option = leadStatusOptionsList.find((item) => item.value === value);
                      setLeadStatus(option?.label || value);
                    }}
                  >
                    <SelectTrigger className="h-8 mt-1 text-sm bg-white dark:bg-slate-950 border-gray-200 dark:border-slate-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {leadStatusOptionsList.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={leadStatus}
                    onChange={(event) => setLeadStatus(event.target.value)}
                    className="h-8 mt-1 text-sm bg-white dark:bg-slate-950 border-gray-200 dark:border-slate-800"
                    placeholder="Lead status"
                  />
                )}
              </div>

              <div>
                <Label className="text-xs text-gray-500 dark:text-gray-400">Source</Label>
                <div className="text-sm font-medium mt-1 text-gray-900 dark:text-white">
                  {contact.source}
                </div>
              </div>

              <div>
                <Label className="text-xs text-gray-500 dark:text-gray-400">Tags</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {(contact.tags || []).map((tag) => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className="bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
                <Input
                  value={tagsText}
                  onChange={(event) => setTagsText(event.target.value)}
                  placeholder="Comma-separated tags"
                  className="h-8 text-sm mt-2 bg-white dark:bg-slate-950 border-gray-200 dark:border-slate-800"
                />
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm">
              <div className="flex items-center justify-between mb-3 gap-2">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                    Custom Attributes
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Structured data to personalize workflows and segments.
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-slate-700 px-2"
                    onClick={onOpenAttributeLibrary}
                  >
                    <Settings className="h-3 w-3 mr-1" /> Library
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 hover:bg-emerald-100 dark:hover:bg-emerald-950/60 px-2"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Add Attribute
                  </Button>
                </div>
              </div>

              {contact.customAttributes.length > 0 ? (
                <div className="space-y-3">
                  {contact.customAttributes.map((attr) => (
                    <div key={attr.key} className="flex flex-col gap-1">
                      <Label className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold">
                        {attr.key}
                      </Label>
                      {attr.type === "dropdown" ? (
                        <Select defaultValue={attr.value}>
                          <SelectTrigger className="h-8 text-sm bg-white dark:bg-slate-950 border-gray-200 dark:border-slate-800">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1-50">1-50</SelectItem>
                            <SelectItem value="50-200">50-200</SelectItem>
                            <SelectItem value="200+">200+</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : attr.type === "date" ? (
                        <Input
                          defaultValue={attr.value}
                          type="date"
                          className="h-8 text-sm bg-white dark:bg-slate-950 border-gray-200 dark:border-slate-800"
                        />
                      ) : (
                        <Input
                          defaultValue={attr.value}
                          className="h-8 text-sm bg-white dark:bg-slate-950 border-gray-200 dark:border-slate-800"
                        />
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 bg-gray-50 dark:bg-slate-950 border border-dashed border-gray-200 dark:border-slate-700 rounded-lg">
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                    No custom attributes.
                  </p>
                  <Button
                    variant="link"
                    className="text-emerald-600 dark:text-emerald-400 h-auto p-0 text-xs mt-1"
                  >
                    Add one now
                  </Button>
                </div>
              )}
            </div>

            <ContactActivityLog activities={contact.activities} />

            <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm">
              <h3 className="font-semibold text-gray-900 dark:text-white text-sm mb-3">
                Campaign History
              </h3>
              {contact.sourceCampaignId ? (
                <div className="border border-gray-200 dark:border-slate-700 rounded-lg p-3 bg-gray-50 dark:bg-slate-950">
                  <div className="flex justify-between items-start mb-2 gap-2">
                    <span className="font-medium text-sm text-gray-900 dark:text-white">
                      {getCampaignNameById(contact.sourceCampaignId)}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Oct 28</span>
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    <Badge className="bg-green-100 dark:bg-green-950/50 text-green-700 dark:text-green-300 px-1.5 py-0 text-[10px] font-normal border-none">
                      Delivered
                    </Badge>
                    <Badge className="bg-blue-100 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 px-1.5 py-0 text-[10px] font-normal border-none">
                      Read
                    </Badge>
                    <Badge className="bg-orange-100 dark:bg-orange-950/50 text-orange-700 dark:text-orange-300 px-1.5 py-0 text-[10px] font-normal border-none">
                      Replied
                    </Badge>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                  No campaigns received yet.
                </p>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
